import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError, getStoredToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, FileDown, MessageCircle, ClipboardList, Trophy, Binoculars, LayoutGrid, RefreshCw, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { exportMatchPdf, exportRivalDossierPdf } from "@/lib/export";
import FeaturedRivalsPicker from "@/components/FeaturedRivalsPicker";
import { PlayerAvatar } from "@/components/PlayerAvatar";
import GuestPlayerPicker from "@/components/GuestPlayerPicker";
import LineupEditor from "@/components/LineupEditor";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CONV_STATUSES = [
  { v: "convocado", label: "Convocado", color: "bg-emerald-500 text-white" },
  { v: "no_convocado", label: "No convocado", color: "bg-slate-500 text-white" },
  { v: "no_disponible", label: "No disponible", color: "bg-amber-600 text-white" },
  { v: "lesion", label: "Lesión", color: "bg-rose-600 text-white" },
];

const LINEUP_STATUSES = [
  { v: "titular", label: "Titular", color: "bg-purple-600 text-white" },
  { v: "suplente", label: "Suplente", color: "bg-sky-500 text-white" },
];

const CALLED = new Set(["convocado", "titular", "suplente"]);

const CONFLICT_LABEL = {
  date: "Fecha", time: "Hora convocatoria", kickoff_time: "Hora", kickoff_location: "Campo",
  kickoff_url: "Mapa", location: "Lugar", rival: "Rival", home_away: "Local/Visitante",
  result: "Resultado", goal_scorers: "Goleadores", cards_summary: "Tarjetas",
  incidents: "Observaciones", status: "Estado convocatoria", minutes: "Minutos",
};

function RffmConflictsCard({ match, items, players, onResolved }) {
  const matchConf = Object.entries(match?.rffm_conflicts || {});
  const convConf = (items || []).flatMap((it) =>
    Object.entries(it.rffm_conflicts || {}).map(([field, c]) => ({ ...c, field, player_id: it.player_id })));
  if (matchConf.length === 0 && convConf.length === 0) return null;
  const pname = (pid) => players.find((p) => p.id === pid)?.name || "Jugador";
  const resolve = async (scope, field, playerId, use) => {
    try {
      await api.post(`/matches/${match.id}/rffm-conflicts/resolve`, { scope, field, player_id: playerId, use });
      toast.success(use === "rffm" ? "Se ha aplicado el dato de la RFFM" : "Se conserva el dato manual");
      onResolved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const Row = ({ label, manual, rffm, onUseRffm, onKeep, testId }) => (
    <div className="flex items-center justify-between flex-wrap gap-2 bg-white rounded-lg border border-amber-200 p-2" data-testid={testId}>
      <div className="text-xs">
        <b>{label}</b>: manual <b className="text-emerald-700">{String(manual ?? "—")}</b> · RFFM <b className="text-purple-700">{String(rffm ?? "—")}</b>
      </div>
      <div className="flex gap-1.5">
        <Button size="sm" variant="outline" className="h-7 text-[11px] border-purple-300 text-purple-700"
          onClick={() => {
            if (window.confirm("¿Sustituir el dato manual por el valor oficial de la RFFM? Solo afecta a este campo.")) onUseRffm();
          }}>
          Usar dato RFFM
        </Button>
        <Button size="sm" variant="ghost" className="h-7 text-[11px] text-slate-600" onClick={onKeep}>
          Mantener manual
        </Button>
      </div>
    </div>
  );
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-2" data-testid="rffm-conflicts-card">
      <div className="text-sm font-black text-amber-900">
        Conflicto detectado. Se ha conservado el dato introducido manualmente.
      </div>
      {matchConf.map(([field, c]) => (
        <Row key={field} label={CONFLICT_LABEL[field] || field} manual={c.manual} rffm={c.rffm}
          onUseRffm={() => resolve("match", field, null, "rffm")}
          onKeep={() => resolve("match", field, null, "manual")}
          testId={`conflict-match-${field}`} />
      ))}
      {convConf.map((c) => (
        <Row key={`${c.player_id}-${c.field}`}
          label={`${pname(c.player_id)} — ${CONFLICT_LABEL[c.field] || c.field}`}
          manual={c.manual} rffm={c.rffm}
          onUseRffm={() => resolve("convocation", c.field, c.player_id, "rffm")}
          onKeep={() => resolve("convocation", c.field, c.player_id, "manual")}
          testId={`conflict-conv-${c.player_id}-${c.field}`} />
      ))}
    </div>
  );
}

function QuickAddEntry({ players, onAdd, withType, disabled, idPrefix }) {
  const [pid, setPid] = useState("");
  const [minute, setMinute] = useState("");
  const [cardType, setCardType] = useState("Amarilla");
  const sorted = [...players].sort((a, b) => (a.dorsal || 99) - (b.dorsal || 99));

  const add = () => {
    const p = players.find((x) => x.id === pid);
    if (!p) return;
    const name = (p.name || "").trim();
    const min = minute ? ` ${minute}'` : "";
    onAdd(withType ? `${cardType} ${name}${min}` : `${name}${min}`);
    setMinute("");
  };

  return (
    <div className="flex items-center gap-2 flex-wrap mb-2">
      <Select value={pid} onValueChange={setPid} disabled={disabled}>
        <SelectTrigger className="w-44 h-8 text-xs" data-testid={`${idPrefix}-player-select`}>
          <SelectValue placeholder="Elegir jugador…" />
        </SelectTrigger>
        <SelectContent>
          {sorted.map((p) => (
            <SelectItem key={p.id} value={p.id}>
              {p.dorsal ? `${p.dorsal} · ` : ""}{p.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {withType && (
        <Select value={cardType} onValueChange={setCardType} disabled={disabled}>
          <SelectTrigger className="w-28 h-8 text-xs" data-testid={`${idPrefix}-type-select`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Amarilla">🟨 Amarilla</SelectItem>
            <SelectItem value="Roja">🟥 Roja</SelectItem>
          </SelectContent>
        </Select>
      )}
      <Input
        type="number"
        min="0"
        max="130"
        placeholder="Min"
        value={minute}
        onChange={(e) => setMinute(e.target.value)}
        className="w-16 h-8 text-xs"
        disabled={disabled}
        data-testid={`${idPrefix}-minute`}
      />
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="h-8 border-purple-300 text-purple-700 hover:bg-purple-50"
        onClick={add}
        disabled={disabled || !pid}
        data-testid={`${idPrefix}-add-btn`}
      >
        + Añadir
      </Button>
    </div>
  );
}

function downloadPdf(apiPath, filename) {
  const token = getStoredToken();
  fetch(`${BACKEND}${apiPath}`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
    .then((r) => r.blob())
    .then((b) => {
      const u = window.URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = u; a.download = filename; a.target = "_top";
      document.body.appendChild(a); a.click();
      setTimeout(() => { a.remove(); window.URL.revokeObjectURL(u); }, 2000);
    })
    .catch(() => toast.error("No se pudo descargar"));
}

function buildWhatsAppText(match, players, rows, teamName) {
  const where = match.home_away === "casa" ? "En casa" : "Fuera";
  const sortByDorsal = (a, b) => (a.dorsal || 99) - (b.dorsal || 99);
  const fmtPlayer = (p) => `• ${p.dorsal ? `#${p.dorsal} ` : ""}${p.name}`;

  const calledList = players
    .filter((p) => CALLED.has(rows[p.id]?.status))
    .sort(sortByDorsal);
  const notCalledList = players
    .filter((p) => {
      const st = rows[p.id]?.status;
      return st && !CALLED.has(st);
    })
    .sort(sortByDorsal);
  const called = calledList.map(fmtPlayer).join("\n");
  const notCalled = notCalledList.map(fmtPlayer).join("\n");

  const lines = [
    `*Convocatoria ${teamName ? `${teamName} ` : ""}— vs ${match.rival}*`,
    `Fecha del partido: ${match.date}`,
    where,
  ];
  if (match.time) lines.push(`Hora de convocatoria: ${match.time}`);
  if (match.location) lines.push(`Lugar de convocatoria: ${match.location}`);
  if (match.kickoff_time) lines.push(`Hora del partido: ${match.kickoff_time}`);
  if (match.kickoff_location) lines.push(`Lugar del partido: ${match.kickoff_location}`);
  if (match.kickoff_url) lines.push(`📍 Cómo llegar al campo:\n${match.kickoff_url}`);

  return (
    lines.join("\n") + "\n\n" +
    `*Convocados (${calledList.length}):*\n${called || "—"}\n\n` +
    `*No convocados (${notCalledList.length}):*\n${notCalled || "—"}\n\n` +
    "¡Nos vemos!"
  );
}

export default function MatchDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [match, setMatch] = useState(null);
  const [team, setTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [rows, setRows] = useState({});
  const [convItems, setConvItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [maxMin, setMaxMin] = useState(90);
  const [teamMax, setTeamMax] = useState(0);
  const [simultaneous, setSimultaneous] = useState(0);
  const [formations, setFormations] = useState([]);
  const [tab, setTab] = useState("convocatoria");

  useEffect(() => {
    api.get(`/matches/${id}/convocation`).then(async (r) => {
      setMatch(r.data.match);
      const pl = await api.get(`/players?team_id=${r.data.match.team_id}`);
      let merged = pl.data;
      if (r.data.match.guest_player_ids?.length) {
        const dir = await api.get("/players/club-directory").catch(() => ({ data: [] }));
        merged = [...pl.data, ...dir.data.filter((g) => r.data.match.guest_player_ids.includes(g.id)).map((g) => ({ ...g, _guest: true }))];
      }
      setPlayers(merged);
      const map = {};
      r.data.items.forEach((it) => (map[it.player_id] = { status: it.status, minutes: it.minutes == null ? "" : it.minutes, reason: it.reason || "" }));
      setConvItems(r.data.items);
      setRows(map);
      const teams = await api.get("/teams");
      const t = teams.data.find((x) => x.id === r.data.match.team_id);
      setTeam(t);
      const meta = await api.get("/meta/categories").catch(() => ({ data: {} }));
      const kw = `${t?.category || ""} ${t?.name || ""}`.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      const kwMin = kw.includes("prebenjamin") ? 40 : kw.includes("benjamin") ? 50 : kw.includes("alevin") ? 50 : kw.includes("infantil") ? 70 : kw.includes("cadete") ? 80 : 90;
      const lim = r.data.limit || {};
      setMaxMin(lim.duration || meta.data.minutes?.[t?.category] || kwMin);
      setTeamMax(lim.max_minutes || 0);
      setSimultaneous(lim.simultaneous || 0);
      const byCat = meta.data.formations_by_category || {};
      const fm = await api.get("/meta/formations", { params: { category: t?.category || "", team_name: t?.name || "" } }).catch(() => ({ data: {} }));
      setFormations(fm.data.for_team || byCat[t?.category] || meta.data.formations || []);
    });
  }, [id]);

  const canEdit = user?.role === "admin" || (user?.team_ids || []).includes(match?.team_id);

  const [syncingActa, setSyncingActa] = useState(false);
  const syncActa = async () => {
    setSyncingActa(true);
    try {
      const r = await api.post(`/matches/${id}/sync-acta`, {}, { timeout: 90000 });
      const d = r.data;
      toast.success(`Acta sincronizada: ${d.titulares} titulares · ${d.suplentes} suplentes · ${d.convocados} sin jugar · ${d.tarjetas} tarjetas`);
      if (d.sin_emparejar?.length) {
        toast.info(`Sin emparejar en la app: ${d.sin_emparejar.join(", ")}`, { duration: 9000 });
      }
      const rc = await api.get(`/matches/${id}/convocation`);
      setMatch(rc.data.match);
      const map = {};
      rc.data.items.forEach((it) => (map[it.player_id] = { status: it.status, minutes: it.minutes || 0, reason: it.reason || "" }));
      setConvItems(rc.data.items);
      setRows(map);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSyncingActa(false);
    }
  };

  const reloadConvocation = async () => {
    const rc = await api.get(`/matches/${id}/convocation`);
    setMatch(rc.data.match);
    const map = {};
    rc.data.items.forEach((it) => (map[it.player_id] = { status: it.status, minutes: it.minutes || 0, reason: it.reason || "" }));
    setConvItems(rc.data.items);
    setRows(map);
  };

  const saveMatchField = async (patch = {}) => {
    if (!match) return;
    const next = { ...match, ...patch };
    try {
      await api.put(`/matches/${match.id}`, {
        team_id: next.team_id,
        date: next.date,
        rival: next.rival,
        home_away: next.home_away,
        result: next.result || "",
        location: next.location || "",
        time: next.time || "",
        kickoff_time: next.kickoff_time || "",
        kickoff_location: next.kickoff_location || "",
        kickoff_url: next.kickoff_url || "",
        formation: next.formation || "",
        objectives_achieved: next.objectives_achieved || "",
        objectives_improve: next.objectives_improve || "",
        best_own_player_id: next.best_own_player_id || null,
        best_rival_player: next.best_rival_player || "",
        best_rival_player_id: next.best_rival_player_id || null,
        incidents: next.incidents || "",
        goal_scorers: next.goal_scorers || "",
        cards_summary: next.cards_summary || "",
        match_type: next.match_type || "Liga",
      });
      setMatch(next);
      toast.success("Guardado");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const appendEntry = (field) => (entry) => {
    const cur = (match[field] || "").trim();
    saveMatchField({ [field]: cur ? `${cur}\n${entry}` : entry });
  };

  const setStatus = (pid, status) =>
    setRows((r) => {
      const prev = r[pid] || { status: "no_convocado", minutes: "" };
      const zeroMin = !CALLED.has(status);
      return { ...r, [pid]: { status, minutes: zeroMin ? 0 : prev.minutes } };
    });

  const setMinutes = (pid, minutes) => {
    setRows((r) => {
      if (minutes === "" || minutes == null) {
        return { ...r, [pid]: { ...(r[pid] || { status: "titular" }), minutes: "" } };
      }
      const m = Math.max(0, Math.min(maxMin, parseInt(minutes, 10) || 0));
      return { ...r, [pid]: { ...(r[pid] || { status: "titular" }), minutes: m } };
    });
  };

  const addGuest = async (p) => {
    await api.post(`/matches/${id}/guests`, { player_id: p.id });
    setPlayers((ps) => [...ps, { ...p, _guest: true }]);
  };

  const removeGuest = async (p) => {
    try {
      await api.delete(`/matches/${id}/guests/${p.id}`);
      setPlayers((ps) => ps.filter((x) => x.id !== p.id));
      setRows((m) => { const n = { ...m }; delete n[p.id]; return n; });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveConvocation = async () => {
    setSaving(true);
    const items = players.map((p) => {
      const cur = rows[p.id] || { status: "no_convocado", minutes: "" };
      const zeroMin = !CALLED.has(cur.status);
      const mins = zeroMin ? 0 : (cur.minutes === "" || cur.minutes == null ? null : Number(cur.minutes));
      return {
        player_id: p.id,
        status: cur.status,
        minutes: mins,
        reason: cur.status === "no_disponible" ? (cur.reason || "") : "",
      };
    });
    try {
      const r = await api.post(`/matches/${id}/convocation`, { items });
      const v = r.data?.validation;
      if (v?.red_alert) {
        toast.warning(`Guardado como borrador: ${v.total}/${v.max_minutes} min (+${v.over_pct}%). Supera el 10% de añadido, no contará en estadísticas hasta corregirlo.`, { duration: 7000 });
      } else if (v?.over) {
        toast.success(`Guardado: ${v.total}/${v.max_minutes} min. El exceso se considera tiempo de añadido (≤10%) y cuenta en estadísticas.`, { duration: 6000 });
      } else {
        toast.success("Guardado");
      }
      setMatch((m) => ({ ...m, minutes_status: v?.status, minutes_sum: v?.total, minutes_max: v?.max_minutes, minutes_red_alert: v?.red_alert }));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const shareWhatsApp = () => {
    const text = buildWhatsAppText(match, players, rows, team?.name || "");
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  const calledPlayers = useMemo(
    () => players.filter((p) => CALLED.has(rows[p.id]?.status)),
    [players, rows]
  );

  const minutesCheck = useMemo(() => {
    const seen = new Set();
    let sum = 0, pending = 0;
    calledPlayers.forEach((p) => {
      if (seen.has(p.id)) return;
      seen.add(p.id);
      const v = rows[p.id]?.minutes;
      if (v === "" || v == null) pending += 1;
      else sum += Number(v) || 0;
    });
    const max = teamMax || 0;
    const diff = sum - max;
    const over = diff > 0;
    const red = max > 0 && sum > max * 1.1;
    const overPct = over && max ? Math.round((diff / max) * 1000) / 10 : 0;
    return { sum, max, diff, over, red, overPct, pending };
  }, [calledPlayers, rows, teamMax]);

  if (!match) return <div className="text-slate-500 text-sm">Cargando...</div>;

  const counts = players.reduce(
    (acc, p) => {
      const s = rows[p.id]?.status || "no_convocado";
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    },
    { titular: 0, suplente: 0, convocado: 0, no_convocado: 0, lesion: 0, no_disponible: 0 }
  );

  return (
    <div className="space-y-6">
      {canEdit && <RffmConflictsCard match={match} items={convItems} players={players} onResolved={reloadConvocation} />}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate("/matches")} data-testid="back-button">
          <ArrowLeft className="h-4 w-4 mr-2" /> Volver
        </Button>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            className="border-slate-300 text-slate-700 hover:bg-slate-50"
            onClick={() => navigate(`/rivals?team_id=${match.team_id}&rival=${encodeURIComponent(match.rival || "")}`)}
            data-testid="match-rival-scouting"
          >
            <Binoculars className="h-4 w-4 mr-2" /> Scouting rival
          </Button>
          {(user?.role === "admin" || user?.role === "viewer") && (
            <Button
              variant="outline"
              className="border-slate-300 text-slate-700 hover:bg-slate-50"
              onClick={() => exportRivalDossierPdf(match.rival)}
              data-testid="match-rival-dossier"
            >
              <FileDown className="h-4 w-4 mr-2" /> Dossier rival PDF
            </Button>
          )}
          <Button variant="outline" onClick={shareWhatsApp} data-testid="share-whatsapp" className="border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10">
            <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp
          </Button>
          <Button variant="outline" onClick={() => exportMatchPdf(match)} data-testid="export-match-detail">
            <FileDown className="h-4 w-4 mr-2" /> Convocatoria PDF
          </Button>
          <Button
            variant="outline"
            onClick={() => downloadPdf(
              `/api/export/match/${match.id}/report-pdf`,
              `partido_${match.date}_${(match.rival || "partido").replace(/\s+/g, "_")}.pdf`,
            )}
            data-testid="export-match-report"
          >
            <FileDown className="h-4 w-4 mr-2" /> Informe partido PDF
          </Button>
          <Button
            variant="outline"
            className="border-purple-300 text-purple-700 hover:bg-purple-50"
            onClick={() => downloadPdf(
              `/api/export/match/${match.id}/registry-pdf`,
              `registro_${match.date}_${(match.rival || "partido").replace(/\s+/g, "_")}.pdf`,
            )}
            data-testid="export-match-registry"
          >
            <Trophy className="h-4 w-4 mr-2" /> Registro PDF
          </Button>
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">
          {match.home_away === "casa" ? "En casa" : "Fuera"} · {match.date}
          {match.jornada && (
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-bold" data-testid="match-detail-jornada">
              Jornada {match.jornada}
            </span>
          )}
          {match.rffm_estado && (
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 font-bold" data-testid="match-detail-rffm-estado">
              {match.rffm_estado}
            </span>
          )}
          {match.rffm_match_id && (
            <span
              className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold"
              title={match.rffm_last_sync ? `Última sincronización: ${new Date(match.rffm_last_sync).toLocaleString("es-ES")}` : ""}
              data-testid="match-detail-rffm-badge"
            >
              ✓ RFFM
            </span>
          )}
          <select
            value={["PRESEASON", "COMPETITION"].includes(match.season_phase) ? match.season_phase : "UNASSIGNED"}
            onChange={async (e) => {
              try {
                await api.post(`/matches/${match.id}/season-phase`, { season_phase: e.target.value });
                setMatch((m) => ({ ...m, season_phase: e.target.value }));
                toast.success("Fase actualizada — estadísticas recalculadas");
              } catch (err) { toast.error(formatApiError(err)); }
            }}
            className={`ml-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold border-0 cursor-pointer ${["PRESEASON", "COMPETITION"].includes(match.season_phase) ? (match.season_phase === "PRESEASON" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-700") : "bg-slate-100 text-slate-500"}`}
            data-testid="match-detail-phase-select"
          >
            {!["PRESEASON", "COMPETITION"].includes(match.season_phase) && <option value="UNASSIGNED" disabled>Fase pendiente</option>}
            <option value="PRESEASON">Pretemporada</option>
            <option value="COMPETITION">Competición</option>
          </select>
        </div>
        <h1 className="text-3xl sm:text-4xl font-black" style={{ fontFamily: "Nunito" }}>
          vs {match.rival}
        </h1>
        {team?.name && (
          <span className="inline-flex items-center px-2 py-0.5 mt-1 rounded-full bg-purple-100 text-purple-800 text-xs font-bold" data-testid="match-detail-team-name">
            {team.name}
          </span>
        )}
        <div className="text-xs text-slate-500 mt-1">
          Categoría: <span className="font-bold text-slate-700">{team?.category || "—"}</span> ·
          {" "}Duración partido: <span className="font-bold text-slate-700">{maxMin}'</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 overflow-x-auto">
        {[
          { key: "convocatoria", label: "Convocatoria", Icon: ClipboardList },
          { key: "partido", label: "Partido", Icon: Trophy },
          { key: "pizarra", label: "Alineación", Icon: LayoutGrid },
        ].map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2 font-bold text-sm -mb-px border-b-2 transition whitespace-nowrap ${
              tab === key ? "border-purple-600 text-purple-700" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid={`tab-${key}`}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "convocatoria" && (
        <div className="space-y-4">
          {/* Meeting fields */}
          <Card className="border-slate-200">
            <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Hora convocatoria</Label>
                <Input type="time" value={match.time || ""} onChange={(e) => setMatch({ ...match, time: e.target.value })} onBlur={(e) => saveMatchField({ time: e.target.value })} disabled={!canEdit} data-testid="match-time-inline" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Lugar convocatoria</Label>
                <Input value={match.location || ""} onChange={(e) => setMatch({ ...match, location: e.target.value })} onBlur={(e) => saveMatchField({ location: e.target.value })} disabled={!canEdit} data-testid="match-location-inline" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Hora del partido</Label>
                <Input type="time" value={match.kickoff_time || ""} onChange={(e) => setMatch({ ...match, kickoff_time: e.target.value })} onBlur={(e) => saveMatchField({ kickoff_time: e.target.value })} disabled={!canEdit} data-testid="match-kickoff-time" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Lugar del partido</Label>
                <Input value={match.kickoff_location || ""} onChange={(e) => setMatch({ ...match, kickoff_location: e.target.value })} onBlur={(e) => saveMatchField({ kickoff_location: e.target.value })} disabled={!canEdit} data-testid="match-kickoff-location" />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">URL del campo (Google Maps, etc.)</Label>
                <div className="flex items-center gap-2">
                  <Input type="url" value={match.kickoff_url || ""} onChange={(e) => setMatch({ ...match, kickoff_url: e.target.value })} onBlur={(e) => saveMatchField({ kickoff_url: e.target.value })} disabled={!canEdit} placeholder="https://maps.google.com/?q=..." data-testid="match-kickoff-url" />
                  {match.kickoff_url && (
                    <a
                      href={match.kickoff_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs whitespace-nowrap text-purple-700 underline hover:text-purple-900"
                      data-testid="match-kickoff-url-open"
                    >
                      Abrir mapa
                    </a>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Card className="border-slate-200"><CardContent className="p-3"><div className="text-xl font-black text-emerald-600">{counts.convocado}</div><div className="text-[10px] uppercase tracking-widest text-slate-500">Convocados</div></CardContent></Card>
            <Card className="border-slate-200"><CardContent className="p-3"><div className="text-xl font-black text-slate-600">{counts.no_convocado}</div><div className="text-[10px] uppercase tracking-widest text-slate-500">No convocados</div></CardContent></Card>
            <Card className="border-slate-200"><CardContent className="p-3"><div className="text-xl font-black text-amber-600">{counts.no_disponible}</div><div className="text-[10px] uppercase tracking-widest text-slate-500">No disponibles</div></CardContent></Card>
            <Card className="border-slate-200"><CardContent className="p-3"><div className="text-xl font-black text-rose-600">{counts.lesion}</div><div className="text-[10px] uppercase tracking-widest text-slate-500">Lesionados</div></CardContent></Card>
          </div>

          {canEdit && (
            <GuestPlayerPicker excludeIds={players.map((p) => p.id)} onAdd={addGuest} />
          )}

          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0 divide-y divide-slate-100">
              {players.map((p) => {
                const cur = rows[p.id] || { status: "no_convocado", minutes: 0 };
                // In Convocatoria tab map titular/suplente → convocado for display
                const displayStatus = CALLED.has(cur.status) ? "convocado" : cur.status;
                return (
                  <div key={p.id} className="p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`conv-row-${p.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <PlayerAvatar player={p} />
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{p.name}</div>
                        <div className="text-xs text-slate-500">
                          {p.position || ""}
                          {p._guest && <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-bold">Invitado · {p.team_name || "otro equipo"}</span>}
                        </div>
                      </div>
                      {p._guest && canEdit && (
                        <button type="button" onClick={() => removeGuest(p)} className="text-slate-400 hover:text-rose-600 text-xs font-bold shrink-0" data-testid={`guest-remove-${p.id}`}>
                          Quitar
                        </button>
                      )}
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      {CONV_STATUSES.map((s) => (
                        <button key={s.v} type="button" disabled={!canEdit} onClick={() => setStatus(p.id, s.v)}
                          className={`px-3 py-1.5 rounded-md text-xs font-bold transition ${displayStatus === s.v ? s.color : "bg-slate-100 text-slate-600 hover:bg-slate-200"} ${!canEdit ? "opacity-60 cursor-not-allowed" : ""}`}
                          data-testid={`conv-${p.id}-${s.v}`}>
                          {s.label}
                        </button>
                      ))}
                    </div>
                    {displayStatus === "no_disponible" && (
                      <div className="basis-full">
                        <Input
                          value={cur.reason || ""}
                          disabled={!canEdit}
                          onChange={(e) => setRows((r) => ({ ...r, [p.id]: { ...(r[p.id] || { status: "no_disponible", minutes: 0 }), reason: e.target.value } }))}
                          placeholder="Motivo (viaje, sanción, trabajo...)"
                          className="h-8 text-xs w-full sm:w-80 border-amber-300 bg-amber-50/40"
                          data-testid={`conv-reason-${p.id}`}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {canEdit && (
            <div className="flex justify-end">
              <Button onClick={saveConvocation} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white font-bold" data-testid="save-convocation-button">
                {saving ? "Guardando..." : "Guardar convocatoria"}
              </Button>
            </div>
          )}
        </div>
      )}

      {tab === "partido" && (
        <div className="space-y-4">
          {/* Match header fields */}
          <Card className="border-slate-200">
            <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Resultado</Label>
                <Input value={match.result || ""} onChange={(e) => setMatch({ ...match, result: e.target.value })} onBlur={(e) => saveMatchField({ result: e.target.value })} disabled={!canEdit} placeholder="Ej. 3-1" data-testid="match-result-inline" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Sistema de juego</Label>
                <Select value={match.formation || "none"} onValueChange={(v) => saveMatchField({ formation: v === "none" ? "" : v })} disabled={!canEdit}>
                  <SelectTrigger data-testid="match-formation"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin especificar</SelectItem>
                    {formations.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Jugador destacado propio</Label>
                <Select value={match.best_own_player_id || "none"} onValueChange={(v) => saveMatchField({ best_own_player_id: v === "none" ? null : v })} disabled={!canEdit}>
                  <SelectTrigger data-testid="match-best-own"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin seleccionar</SelectItem>
                    {players.map((p) => <SelectItem key={p.id} value={p.id}>{p.dorsal ? `#${p.dorsal} ` : ""}{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Jugadores destacados rivales</Label>
                <FeaturedRivalsPicker matchId={match.id} matchRival={match.rival || ""} disabled={!canEdit} />
                <p className="text-[10px] text-slate-400 mt-1">
                  Puedes destacar varios jugadores. Se muestran primero los del rival del partido. Pulsa un nombre para ver su dashboard.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Lineup + minutes */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
              <CardTitle className="text-base font-black" style={{ fontFamily: "Nunito" }}>Alineación y minutos</CardTitle>
              {match.rffm_match_id && canEdit && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-purple-300 text-purple-700 font-bold hover:bg-purple-50"
                  onClick={syncActa}
                  disabled={syncingActa}
                  data-testid="sync-acta-btn"
                >
                  {syncingActa ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
                  Sincronizar acta RFFM
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-0 divide-y divide-slate-100">
              {calledPlayers.length > 0 && (
                <div className="p-4 bg-slate-50" data-testid="minutes-counter-block">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="text-sm font-black" style={{ fontFamily: "Nunito" }}>
                      Minutos registrados:{" "}
                      <span className={minutesCheck.over ? "text-rose-700" : "text-emerald-700"} data-testid="minutes-counter">
                        {minutesCheck.sum}
                      </span>{" "}
                      de {minutesCheck.max}{" "}
                      <span className={minutesCheck.diff > 0 ? "text-rose-700" : "text-slate-500"}>
                        ({minutesCheck.diff >= 0 ? "+" : ""}{minutesCheck.diff})
                      </span>
                    </div>
                    {simultaneous > 0 && (
                      <span className="text-[11px] text-slate-400">Máximo = {simultaneous} jugadores × {maxMin}' = {minutesCheck.max}'</span>
                    )}
                  </div>
                  {minutesCheck.red && (
                    <div className="mt-2 flex items-start gap-2 rounded-lg border-2 border-rose-500 bg-rose-50 px-3 py-2 text-sm font-bold text-rose-800" data-testid="minutes-red-alert">
                      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>¡Exceso grave! Has superado el máximo en <b>{minutesCheck.diff} min ({minutesCheck.overPct}%)</b>, por encima del 10% de añadido permitido. El partido queda como borrador y no cuenta en estadísticas hasta corregirlo.</span>
                    </div>
                  )}
                  {minutesCheck.over && !minutesCheck.red && (
                    <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="minutes-over-warning">
                      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>Has registrado <b>{minutesCheck.sum}</b> de <b>{minutesCheck.max}</b> minutos (sobran <b>{minutesCheck.diff}</b>). Dentro del 10% de tiempo de añadido permitido: <b>cuenta en estadísticas</b>.</span>
                    </div>
                  )}
                  {!minutesCheck.over && minutesCheck.pending > 0 && (
                    <div className="mt-2 text-xs text-slate-500" data-testid="minutes-pending">
                      Faltan <b>{minutesCheck.pending}</b> jugador{minutesCheck.pending !== 1 ? "es" : ""} por registrar minutos (campos vacíos resaltados). Una suma inferior al máximo puede ser correcta (expulsión, menos efectivos).
                    </div>
                  )}
                </div>
              )}
              {calledPlayers.length === 0 && <div className="p-6 text-sm text-slate-500">No hay jugadores convocados. Ve a la pestaña "Convocatoria" para convocarlos.</div>}
              {calledPlayers.map((p) => {
                const cur = rows[p.id] || { status: "convocado", minutes: "" };
                const isEmpty = cur.minutes === "" || cur.minutes == null;
                const pct = maxMin && !isEmpty ? Math.round((Number(cur.minutes) / maxMin) * 100) : 0;
                return (
                  <div key={p.id} className="p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`lineup-row-${p.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <PlayerAvatar player={p} />
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{p.name}</div>
                        <div className="text-xs text-slate-500">{p.position || ""}</div>
                      </div>
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      {LINEUP_STATUSES.map((s) => (
                        <button key={s.v} type="button" disabled={!canEdit} onClick={() => setStatus(p.id, s.v)}
                          className={`px-3 py-1.5 rounded-md text-xs font-bold transition ${cur.status === s.v ? s.color : "bg-slate-100 text-slate-600 hover:bg-slate-200"} ${!canEdit ? "opacity-60 cursor-not-allowed" : ""}`}
                          data-testid={`lineup-${p.id}-${s.v}`}>
                          {s.label}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <Input type="number" className={`w-20 ${isEmpty ? "border-amber-400 bg-amber-50" : ""}`} min={0} max={maxMin}
                        placeholder="—" value={isEmpty ? "" : cur.minutes} disabled={!canEdit}
                        onChange={(e) => setMinutes(p.id, e.target.value)} data-testid={`minutes-${p.id}`} />
                      <span className="text-xs text-slate-500">/ {maxMin}'</span>
                      <span className={`text-xs font-bold ${isEmpty ? "text-amber-600" : pct >= 75 ? "text-emerald-700" : pct >= 50 ? "text-amber-700" : "text-slate-500"}`}>{isEmpty ? "pendiente" : `${pct}%`}</span>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Match log (goleadores, tarjetas, tipo) */}
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label>Tipo de partido</Label>
                  <Select value={match.match_type || "Liga"} onValueChange={(v) => saveMatchField({ match_type: v })} disabled={!canEdit}>
                    <SelectTrigger data-testid="match-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Liga","Copa","Amistoso","Torneo","Pretemporada"].map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Goleadores propios</Label>
                  <QuickAddEntry
                    players={players}
                    onAdd={appendEntry("goal_scorers")}
                    disabled={!canEdit}
                    idPrefix="goal-scorer"
                  />
                  <Textarea
                    rows={3}
                    placeholder="Pérez 12', Pérez 34', López 67'"
                    value={match.goal_scorers || ""}
                    onChange={(e) => setMatch({ ...match, goal_scorers: e.target.value })}
                    onBlur={(e) => saveMatchField({ goal_scorers: e.target.value })}
                    disabled={!canEdit}
                    data-testid="match-goal-scorers"
                  />
                  <p className="text-[11px] text-slate-400 mt-1">Elige un jugador y pulsa Añadir, o escríbelos separados por coma o salto de línea.</p>
                </div>
                <div>
                  <Label>Tarjetas</Label>
                  <QuickAddEntry
                    players={players}
                    onAdd={appendEntry("cards_summary")}
                    withType
                    disabled={!canEdit}
                    idPrefix="card"
                  />
                  <Textarea
                    rows={3}
                    placeholder="Amarilla García 22', Roja Pérez 78'"
                    value={match.cards_summary || ""}
                    onChange={(e) => setMatch({ ...match, cards_summary: e.target.value })}
                    onBlur={(e) => saveMatchField({ cards_summary: e.target.value })}
                    disabled={!canEdit}
                    data-testid="match-cards-summary"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Objectives + incidents */}
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-4 space-y-4">
              <div>
                <Label>Objetivos conseguidos</Label>
                <Textarea rows={3} value={match.objectives_achieved || ""} onChange={(e) => setMatch({ ...match, objectives_achieved: e.target.value })} onBlur={(e) => saveMatchField({ objectives_achieved: e.target.value })} disabled={!canEdit} data-testid="match-obj-achieved" />
              </div>
              <div>
                <Label>Objetivos a mejorar</Label>
                <Textarea rows={3} value={match.objectives_improve || ""} onChange={(e) => setMatch({ ...match, objectives_improve: e.target.value })} onBlur={(e) => saveMatchField({ objectives_improve: e.target.value })} disabled={!canEdit} data-testid="match-obj-improve" />
              </div>
              <div>
                <Label>Incidencias</Label>
                <Textarea rows={3} value={match.incidents || ""} onChange={(e) => setMatch({ ...match, incidents: e.target.value })} onBlur={(e) => saveMatchField({ incidents: e.target.value })} disabled={!canEdit} data-testid="match-incidents" />
              </div>
            </CardContent>
          </Card>

          {canEdit && (
            <div className="flex justify-end">
              <Button onClick={saveConvocation} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white font-bold" data-testid="save-lineup-button">
                {saving ? "Guardando..." : "Guardar alineación y minutos"}
              </Button>
            </div>
          )}
        </div>
      )}

      {tab === "pizarra" && (
        <LineupEditor matchId={id} canEdit={canEdit} />
      )}
    </div>
  );
}
